import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService, User } from './auth.service';

export interface ProfileUpdateData {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone_number?: string;
}

export interface PasswordUpdateData {
  current_password: string;
  password: string;
}

export interface TopUpData {
  amount: number;
  paymentMethod: 'card' | 'bankTransfer' | 'phone';
}

export interface TopUpResponse {
  message: string;
  balance: number;
  paymentMethod: string;
  receiptSent: boolean;
}

export interface BalanceResponse {
  balance: number;
}

export interface FreeRelistCredits {
  has_free_relist_credits: boolean;
  available_credits: number;
  active_credits?: number;
  expired_credits?: number;
  message: string;
  details?: Array<{
    listing_id: number;
    title: string;
    category: string;
    subcategory: string;
    status: string;
    expires_at: string;
  }>;
}

export interface FreeRelistResponse {
  status: string;
  has_free_relist_credits: boolean;
  available_credits: number;
  active_credits?: number;
  expired_credits?: number;
  message: string;
  details?: Array<{
    listing_id: number;
    title: string;
    category: string;
    subcategory: string;
    status: string;
    expires_at: string;
  }>;
}

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient, private authService: AuthService) {}

  getCurrentUserProfile(): Observable<User> {
    return this.http.get<User>(`${this.apiUrl}/users/me`).pipe(
      tap((user) => {
        this.authService.updateCurrentUser(user);
      }),
      catchError((error) => {
        console.error('Error fetching current user', error);
        if (error.status === 401 || error.status === 403) {
          this.authService.clearAuthData();
        }
        return throwError(() => new Error('Failed to load user data'));
      })
    );
  }

  updateUserProfile(updateData: ProfileUpdateData): Observable<any> {
    return this.http.put(`${this.apiUrl}/users/profile`, updateData).pipe(
      tap((response: any) => {
        if (response.passwordChanged) {
          this.authService.handlePasswordChangeResponse(response);
        } else if (response.user) {
          this.authService.updateCurrentUser(response.user);
        }
      }),
      catchError((error) => {
        let errorMessage = 'Failed to update profile';
        if (error.error && error.error.message) {
          errorMessage = error.error.message;
        }
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  initiateEmailChange(newEmail: string): Observable<any> {
    return this.http
      .post(`${this.apiUrl}/users/change-email`, { new_email: newEmail })
      .pipe(
        catchError((error) => {
          let errorMessage = 'Failed to initiate email change';
          if (error.error && error.error.message) {
            errorMessage = error.error.message;
          }
          return throwError(() => new Error(errorMessage));
        })
      );
  }

  verifyEmailChange(newEmail: string, code: string): Observable<any> {
    return this.http
      .post(`${this.apiUrl}/users/verify-email-change`, {
        new_email: newEmail,
        code: code,
      })
      .pipe(
        tap(() => {
          this.getCurrentUserProfile().subscribe();
        }),
        catchError((error) => {
          let errorMessage = 'Failed to verify email change';
          if (error.error && error.error.message) {
            errorMessage = error.error.message;
          }
          return throwError(() => new Error(errorMessage));
        })
      );
  }

  revertEmailChange(token: string): Observable<any> {
    return this.http
      .get(`${this.apiUrl}/users/revert-email-change/${token}`)
      .pipe(
        tap(() => {
          this.getCurrentUserProfile().subscribe();
        }),
        catchError((error) => {
          let errorMessage = 'Failed to revert email change';
          if (error.error && error.error.message) {
            errorMessage = error.error.message;
          }
          return throwError(() => new Error(errorMessage));
        })
      );
  }

  updatePassword(
    currentPassword: string,
    newPassword: string
  ): Observable<any> {
    const updateData: PasswordUpdateData = {
      current_password: currentPassword,
      password: newPassword,
    };

    return this.http.put(`${this.apiUrl}/users/profile`, updateData).pipe(
      catchError((error) => {
        let errorMessage = 'Failed to update password';

        if (error.status === 401) {
          errorMessage = 'Current password is incorrect';
        } else if (error.error && error.error.message) {
          errorMessage = error.error.message;
        }

        return throwError(() => new Error(errorMessage));
      })
    );
  }

  getUserBalance(): Observable<number> {
    return this.http.get<BalanceResponse>(`${this.apiUrl}/users/balance`).pipe(
      map((response) => response.balance),
      tap((balance) => {
        const currentUser = this.authService.getCurrentUserValue();
        if (currentUser) {
          currentUser.balance = balance;
          this.authService.updateCurrentUser(currentUser);
        }
      }),
      catchError((error) => {
        console.error('Error fetching balance', error);
        return throwError(() => new Error('Failed to load balance'));
      })
    );
  }

  topUpBalance(data: TopUpData): Observable<{
    balance: number;
    receiptSent?: boolean;
    paymentMethod?: string;
  }> {
    return this.http
      .post<TopUpResponse>(`${this.apiUrl}/users/topup`, data)
      .pipe(
        map((response) => ({
          balance: response.balance,
          receiptSent: response.receiptSent || false,
          paymentMethod: response.paymentMethod,
        })),
        tap((result) => {
          const currentUser = this.authService.getCurrentUserValue();
          if (currentUser) {
            currentUser.balance = result.balance;
            this.authService.updateCurrentUser(currentUser);
          }
        }),
        catchError((error) => {
          let errorMessage = 'Failed to top up balance';
          if (error.error && error.error.message) {
            errorMessage = error.error.message;
          }
          return throwError(() => new Error(errorMessage));
        })
      );
  }

  updateBalanceAfterPurchase(newBalance: number): void {
    const currentUser = this.authService.getCurrentUserValue();
    if (currentUser) {
      currentUser.balance = newBalance;
      this.authService.updateCurrentUser(currentUser);
    }
  }

  canAffordPlan(planPrice: number): Observable<boolean> {
    return this.getUserBalance().pipe(
      map((balance) => balance >= planPrice),
      catchError(() => {
        return [false];
      })
    );
  }

  getFormattedBalance(balance?: number): string {
    if (balance === undefined || balance === null) {
      return '$0.00';
    }
    return `$${balance.toFixed(2)}`;
  }

  refreshUserData(): Observable<User> {
    return this.getCurrentUserProfile();
  }

  hasMinimumBalance(minimumAmount: number = 0): Observable<boolean> {
    return this.getUserBalance().pipe(
      map((balance) => balance >= minimumAmount),
      catchError(() => [false])
    );
  }

  calculateBalanceAfterPurchase(planPrice: number): Observable<number | null> {
    return this.getUserBalance().pipe(
      map((currentBalance) => {
        if (currentBalance >= planPrice) {
          return currentBalance - planPrice;
        }
        return null;
      }),
      catchError(() => [null])
    );
  }

  checkFreeRelistCredits(): Observable<FreeRelistCredits> {
    return this.http
      .get<FreeRelistResponse>(`${this.apiUrl}/relist/credits`)
      .pipe(
        map((response) => ({
          has_free_relist_credits: response.has_free_relist_credits,
          available_credits: response.available_credits,
          active_credits: response.active_credits,
          expired_credits: response.expired_credits,
          message: response.message,
          details: response.details,
        })),
        catchError((error) => {
          console.error('Error checking free relist credits:', error);
          return of({
            has_free_relist_credits: false,
            available_credits: 0,
            active_credits: 0,
            expired_credits: 0,
            message: 'Failed to check free relist credits',
          });
        })
      );
  }

  getAvailableRelists(): Observable<any[]> {
    return this.http
      .get<{ status: string; available_relists: any[] }>(
        `${this.apiUrl}/relist/available`
      )
      .pipe(
        map((response) => response.available_relists || []),
        catchError((error) => {
          console.error('Error fetching available relists:', error);
          return of([]);
        })
      );
  }
}
